/**
 * File scraped and trimmed from BulkUpload component
 */
import _ from 'lodash';
import XLSX from 'xlsx';

const requiredHeadersAsStringArray = (acc: string[][], curr: ImporterColumn) => {
    if (!curr?.acceptableHeaders) return acc;
    if (Array.isArray(curr?.acceptableHeaders)) {
        return acc.concat([curr?.acceptableHeaders]);
    }
    return acc.concat([[curr?.acceptableHeaders]]);
};

const getHeaderRow = (sheet: any): string[] => {
    const headers = [];
    const range = XLSX.utils.decode_range(sheet['!ref']);
    const R = range.s.r; /* start in the first row */

    let C;
    /* walk every column in the range */
    for (C = range.s.c; C <= range.e.c; ++C) {
        const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })]; /* find the cell in the first row */

        let hdr = `UNKNOWN ${C}`; // <-- replace with your desired default
        if (cell?.t) hdr = XLSX.utils.format_cell(cell);

        headers.push(hdr);
    }
    return headers;
};

export const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // eslint-disable-next-line
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const lowercaseHeaders = (workSheet: XLSX.WorkSheet, columns: ImporterColumn[]) => {
    // workSheet headers is [a, b, c, d]
    const workSheetHeaders = getHeaderRow(workSheet);
    // need the headers to match the acceptable columns that come from config (in order)
    const header = workSheetHeaders
        // we don't care about casing for headers
        .map((v) => v.toLowerCase())
        .map((singleHeader) => {
            const v = columns.find((o) => o?.acceptableHeaders?.includes(singleHeader));
            if (v?.accessor == null) {
                return null;
            }
            return v.accessor;
        });

    return header as string[];
};

/** Validate header row of csv file.
 * Returns difference array if columns are missing, or isEmptyFile as true if we have header but no data
 * @param workSheet single sheet of data to be validated
 * @param columns required columns to be validated against worksheet data
 */
export const validateCsvHeaders = (workSheet: XLSX.WorkSheet, columns: ImporterColumn[]) => {
    const requiredColumns = columns.reduce(requiredHeadersAsStringArray, []);

    // workSheet headers is [a, b, c, d]
    const workSheetHeaders = getHeaderRow(workSheet);

    const header = lowercaseHeaders(workSheet, columns);
    const hasAtLeastOneHeader = !_.every(header, (v) => v === null);

    // assume user has given us tiny file so reading should be okay
    const workSheetAsJson = XLSX.utils
        // raw false converts all data to string
        .sheet_to_json(workSheet, { defval: '', header, raw: false })
        // strip out the header row
        ?.slice(1);
    const lowercaseColumnsArray = workSheetHeaders.map((v) => v.toLowerCase());
    const difference = requiredColumns.filter((requiredHeader) => {
        // for the current required headers, there should be some value
        // inside of columnsArray that maps to it. If not, this "difference" in required headers and
        // the csv headers means a required header is missing.
        return !lowercaseColumnsArray.some((b) => requiredHeader.includes(b as any));
    });

    if (!workSheetAsJson.length && hasAtLeastOneHeader) {
        return {
            difference: requiredColumns,
            isEmptyFile: true
        };
    }

    return {
        difference
    };
};
